/**
 * Media slider (Pro)
 * - Native scroll-snap track, so swipe works on touch devices with no library.
 * - Autoplay, dots, arrows, pause on hover / when tab hidden.
 * - Each slide can have separate desktop and mobile media; only the video that
 *   is visible on the current screen size and on the active slide plays.
 */
if (!customElements.get('media-slider-pro')) {
  class MediaSliderPro extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
      this.index = 0;
      this.speed = Math.max(Number(this.dataset.speed || 5), 2) * 1000;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.mobileQuery = window.matchMedia('(max-width: 749px)');

      this.querySelector('[data-slider-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-slider-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));

      this.onScroll = () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.syncFromScroll(), 90);
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.onMediaChange = () => this.updateVideos();
      this.mobileQuery.addEventListener('change', this.onMediaChange);

      this.addEventListener('pointerenter', () => this.stop());
      this.addEventListener('pointerleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());

      this.onVisibility = () => (document.hidden ? this.stop() : this.start());
      document.addEventListener('visibilitychange', this.onVisibility);

      // Theme editor: jump to the slide being edited
      this.addEventListener('shopify:block:select', (event) => {
        const i = this.slides.indexOf(event.target.closest('[data-slide]'));
        if (i >= 0) {
          this.editing = true;
          this.stop();
          this.go(i, false, 'instant');
        }
      });
      this.addEventListener('shopify:block:deselect', () => {
        this.editing = false;
        this.start();
      });

      this.update();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.track?.removeEventListener('scroll', this.onScroll);
      this.mobileQuery?.removeEventListener('change', this.onMediaChange);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    go(i, fromUser = false, behavior = 'smooth') {
      const count = this.slides.length;
      if (!count) return;
      this.index = (i + count) % count;
      this.track.scrollTo({ left: this.targetFor(this.slides[this.index]), behavior });
      this.update();
      if (fromUser) this.restart();
    }

    /** Scroll position that centers the slide (equals offsetLeft for full-width slides) */
    targetFor(slide) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const centered = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
      return Math.max(0, Math.min(max, centered));
    }

    syncFromScroll() {
      const left = this.track.scrollLeft;
      let closest = 0;
      let best = Infinity;
      this.slides.forEach((slide, i) => {
        const distance = Math.abs(this.targetFor(slide) - left);
        if (distance < best) {
          best = distance;
          closest = i;
        }
      });
      if (closest !== this.index) {
        this.index = closest;
        this.update();
      }
    }

    update() {
      this.dots.forEach((dot, i) => {
        const active = i === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
      this.slides.forEach((slide, i) => slide.toggleAttribute('data-active', i === this.index));
      this.updateVideos();
    }

    updateVideos() {
      this.slides.forEach((slide, i) => {
        slide.querySelectorAll('video').forEach((video) => {
          const visible = video.offsetParent !== null || video.getClientRects().length > 0;
          if (i === this.index && visible) {
            if (video.preload === 'none') video.preload = 'auto';
            const playing = video.play();
            if (playing && playing.catch) playing.catch(() => {});
          } else {
            video.pause();
          }
        });
      });
    }

    start() {
      if (!this.autoplay || this.editing || this.timer) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }
  }

  customElements.define('media-slider-pro', MediaSliderPro);
}
